import { useMemo, useRef, useState, type ReactNode } from 'react'
import { ArrowRight, Dices, Search } from 'lucide-react'
import { motion } from 'motion/react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { PageHero } from '@/components/dashboard/fx/PageHero'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { EASE } from '@/components/dashboard/fx/motion'
import { PAGE_WIDTH } from '@/components/dashboard/fx/layout'
import { DISCIPLINE_TABS } from '@/components/dashboard/DisciplineDock'
import type { PlaylistOptions } from '@/components/build/types'
import { useAllInstructors, type InstructorSummary } from '@/hooks/useAllInstructors'
import { usePlaylistOptions } from '@/hooks/usePlaylistOptions'
import { formatPrecise, initials } from '@/lib/format'

export interface CompleteSetup {
  instructorId: string
  discipline: string
  minutes: number
  theme: string | null
  format: string | null
}

const SECONDS_PER_SONG = 195 // for the "about N songs" hint; the server uses real averages
const TOP_INSTRUCTORS = 12

function Step({ number, title, hint, children }: { number: string; title: string; hint?: string; children: ReactNode }) {
  return (
    <motion.section initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="scroll-mt-28">
      <div className="mb-5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-primary">{number}</span>
        <h2 className="font-display text-3xl font-semibold tracking-tighter sm:text-4xl">{title}</h2>
        {hint && <span className="text-sm text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </motion.section>
  )
}

function Chip({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`relative rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${
        selected ? 'border-transparent text-primary-foreground' : 'border-border bg-card/60 text-foreground hover:border-primary/50'
      }`}
    >
      {selected && <motion.span layoutId={`chip-bg-${String(children)}`} className="absolute inset-0 rounded-full bg-primary" transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }} />}
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </button>
  )
}

function InstructorTile({ instructor, selected, onPick }: { instructor: InstructorSummary; selected: boolean; onPick: () => void }) {
  return (
    <button type="button" onClick={onPick} aria-pressed={selected} className="group block h-full w-full text-left">
      <SpotlightCard className={`flex h-full items-center gap-3 rounded-2xl p-3.5 transition-all duration-300 group-hover:-translate-y-0.5 ${selected ? 'ring-2 ring-primary' : ''}`}>
        <Avatar className={`size-11 shrink-0 transition duration-500 ${selected ? '' : 'grayscale group-hover:grayscale-0'}`}>
          <AvatarImage src={instructor.image_url ?? undefined} alt="" />
          <AvatarFallback className="text-xs">{initials(instructor.name)}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium">{instructor.name}</span>
          <span className="text-xs text-muted-foreground">{formatPrecise(instructor.ride_count)} classes</span>
        </div>
      </SpotlightCard>
    </button>
  )
}

interface SetupWizardProps {
  onBuild: (setup: CompleteSetup) => void
}

export function SetupWizard({ onBuild }: SetupWizardProps) {
  const { data: instructors } = useAllInstructors()
  const [query, setQuery] = useState('')
  const [instructorId, setInstructorId] = useState<string | null>(null)
  const [discipline, setDiscipline] = useState<string | null>(null)
  const [format, setFormat] = useState<string | null>(null)
  const [minutes, setMinutes] = useState<number | null>(null)
  const [theme, setTheme] = useState<string | null>(null)
  const [surprising, setSurprising] = useState(false)
  const workoutRef = useRef<HTMLDivElement>(null)
  const lengthRef = useRef<HTMLDivElement>(null)

  const { data: options } = usePlaylistOptions(instructorId)
  const disciplines = useMemo(() => options?.disciplines.filter((d) => d.durations.length > 0) ?? [], [options])
  const current = disciplines.find((d) => d.key === discipline)
  const instructor = instructors_find(instructors, instructorId)

  const visible = useMemo(() => {
    if (!instructors) return null
    const q = query.trim().toLowerCase()
    return q ? instructors.filter((i) => i.name.toLowerCase().includes(q)).slice(0, 24) : instructors.slice(0, TOP_INSTRUCTORS)
  }, [instructors, query])

  const scrollTo = (ref: React.RefObject<HTMLDivElement | null>) => setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350)

  const chooseInstructor = (id: string) => {
    setInstructorId(id)
    setDiscipline(null)
    setFormat(null)
    setMinutes(null)
    setTheme(null)
    scrollTo(workoutRef)
  }

  const chooseDiscipline = (key: string) => {
    setDiscipline(key)
    setFormat(null)
    setMinutes(null)
    setTheme(null)
    scrollTo(lengthRef)
  }

  // Random instructor, then a random combination they really teach.
  const surprise = async () => {
    if (!instructors?.length) return
    setSurprising(true)
    try {
      const pool = instructors.slice(0, 40)
      for (let attempt = 0; attempt < 5; attempt++) {
        const pick = pool[Math.floor(Math.random() * pool.length)]
        const response = await fetch(`/api/playlist_builder/${pick.id}/options`, { headers: { Accept: 'application/json' } })
        const found = (await response.json()) as PlaylistOptions
        const usable = found.disciplines.filter((d) => d.durations.length > 0)
        if (usable.length === 0) continue
        const chosen = usable[Math.floor(Math.random() * Math.min(usable.length, 2))]
        const lengths = chosen.durations.filter((d) => [20, 30, 45].includes(d.minutes))
        const length = (lengths.length ? lengths : chosen.durations)[Math.floor(Math.random() * (lengths.length || chosen.durations.length))]
        const themes = chosen.themes.slice(0, 8)
        onBuild({
          instructorId: pick.id,
          discipline: chosen.key,
          minutes: length.minutes,
          theme: themes.length ? themes[Math.floor(Math.random() * themes.length)].name : null,
          format: null,
        })
        return
      }
    } finally {
      setSurprising(false)
    }
  }

  const ready = Boolean(instructorId && discipline && minutes)

  return (
    <div className="overflow-x-clip">
      <PageHero
        eyebrow="Ride builder"
        title="Build a"
        accent="ride."
        description="Pick an instructor, a workout and a length. We'll deal you a setlist built from what they play, what they don't -- and the essentials they've never touched. Then make it yours."
      >
        <div className={`${PAGE_WIDTH} pb-10`}>
          <button
            type="button"
            onClick={surprise}
            disabled={surprising || !instructors}
            className="group inline-flex items-center gap-2.5 rounded-full border border-border bg-background/60 px-5 py-3 font-medium backdrop-blur transition-all hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10 disabled:opacity-60"
          >
            <motion.span animate={surprising ? { rotate: 360 } : {}} transition={{ duration: 0.8, repeat: surprising ? Infinity : 0, ease: 'linear' }} className="flex">
              <Dices className="size-5 text-primary" />
            </motion.span>
            {surprising ? 'Rolling…' : 'Surprise me'}
          </button>
        </div>
      </PageHero>

      <div className={`${PAGE_WIDTH} flex flex-col gap-16 py-14 sm:py-20`}>
        <Step number="01" title="Who's on the mic?" hint="Whose taste are we building around">
          <div className="relative mb-5 max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search instructors…" className="h-12 rounded-full bg-card/50 pl-11 backdrop-blur" />
          </div>
          {visible ? (
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {visible.map((i) => (
                <li key={i.id}>
                  <InstructorTile instructor={i} selected={i.id === instructorId} onPick={() => chooseInstructor(i.id)} />
                </li>
              ))}
              {visible.length === 0 && <li className="col-span-full text-sm text-muted-foreground">No instructors match &ldquo;{query}&rdquo;</li>}
            </ul>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-[74px] rounded-2xl" />
              ))}
            </div>
          )}
        </Step>

        {instructorId && (
          <div ref={workoutRef}>
            <Step number="02" title="What kind of workout?" hint={instructor ? `What ${instructor.name.split(' ')[0]} actually teaches` : undefined}>
              {options ? (
                <div className="flex flex-col gap-5">
                  <div className="flex flex-wrap gap-2.5">
                    {disciplines.map((d) => {
                      const tab = DISCIPLINE_TABS.find((t) => t.value === d.key)
                      const Icon = tab?.icon
                      return (
                        <Chip key={d.key} selected={discipline === d.key} onClick={() => chooseDiscipline(d.key)}>
                          {Icon && <Icon className="size-4" />}
                          {tab?.label ?? d.key}
                          <span className="text-xs opacity-70">{formatPrecise(d.rides)}</span>
                        </Chip>
                      )
                    })}
                  </div>
                  {current && current.formats.length > 0 && (
                    <Reveal y={16} className="flex flex-col gap-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Format (optional)</span>
                      <div className="flex flex-wrap gap-2">
                        <Chip selected={format === null} onClick={() => setFormat(null)}>
                          Any format
                        </Chip>
                        {current.formats.slice(0, 8).map((f) => (
                          <Chip key={f.id} selected={format === f.id} onClick={() => setFormat(f.id)}>
                            {f.name}
                          </Chip>
                        ))}
                      </div>
                    </Reveal>
                  )}
                </div>
              ) : (
                <Skeleton className="h-12 w-2/3 rounded-full" />
              )}
            </Step>
          </div>
        )}

        {current && (
          <div ref={lengthRef}>
            <Step number="03" title="How long?">
              <div className="flex flex-wrap gap-3">
                {current.durations.map((d) => {
                  const selected = minutes === d.minutes
                  return (
                    <button
                      key={d.minutes}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setMinutes(d.minutes)}
                      className={`flex min-w-24 flex-col items-center rounded-2xl border px-5 py-4 transition-all ${
                        selected ? 'border-primary bg-primary text-primary-foreground shadow-lg shadow-primary/25' : 'border-border bg-card/60 hover:-translate-y-0.5 hover:border-primary/50'
                      }`}
                    >
                      <span className="font-display text-3xl font-semibold tabular-nums leading-none">{d.minutes}</span>
                      <span className="mt-1 text-xs opacity-80">minutes</span>
                      <span className="mt-2 text-[11px] opacity-70">≈ {Math.max(2, Math.round((d.minutes * 60) / SECONDS_PER_SONG))} songs</span>
                    </button>
                  )
                })}
              </div>
            </Step>
          </div>
        )}

        {current && minutes && (
          <Step number="04" title="What's the vibe?" hint="Optional -- skip it for anything goes">
            <div className="flex flex-wrap gap-2.5">
              <Chip selected={theme === null} onClick={() => setTheme(null)}>
                Anything goes
              </Chip>
              {current.themes.map((t) => (
                <Chip key={t.name} selected={theme === t.name} onClick={() => setTheme(t.name)}>
                  {t.name}
                  <span className="text-xs opacity-70">{t.rides}</span>
                </Chip>
              ))}
            </div>
          </Step>
        )}

        {ready && (
          <Reveal y={24} className="flex justify-start">
            <button
              type="button"
              onClick={() => onBuild({ instructorId: instructorId!, discipline: discipline!, minutes: minutes!, theme, format })}
              className="group inline-flex items-center gap-3 rounded-full bg-primary px-8 py-4 font-display text-xl font-semibold text-primary-foreground shadow-xl shadow-primary/30 transition-transform hover:scale-105"
            >
              Build my ride
              <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
            </button>
          </Reveal>
        )}
      </div>
    </div>
  )
}

function instructors_find(list: InstructorSummary[] | null, id: string | null) {
  return id && list ? list.find((i) => i.id === id) : undefined
}
